import { useCallback, useEffect, useMemo, type ReactNode } from 'react';
import { useIsAuthenticated } from '@azure/msal-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  canSeeMenuItem,
  levelSatisfies,
  type AuthMeResponse,
  type RbacArea,
  type RbacLevel,
} from '@catalyst-psa/shared';

import { api } from '../api/client.js';
import { AuthContext, type AuthContextValue } from './authContext.js';
import { getImpersonatedMemberId, setImpersonatedMemberId } from './impersonation.js';

interface AuthProviderProps {
  children: ReactNode;
}

/** Loads the current principal from the API and exposes RBAC helpers via context. */
export function AuthProvider({ children }: AuthProviderProps): JSX.Element {
  const isAuthenticated = useIsAuthenticated();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: () => api.get<AuthMeResponse>('/auth/me'),
    enabled: isAuthenticated,
  });

  const principal = query.data?.principal ?? null;
  const roles = query.data?.roles ?? [];
  const impersonator = query.data?.impersonator ?? null;

  // Self-heal a broken impersonation so a bad stored target can never lock the
  // user out. If `/auth/me` fails while impersonating, the header is likely the
  // cause (target removed, transient deploy, etc.) — drop it and reload as the
  // real identity. If it succeeds but the backend reports no active
  // impersonation, the stored target is stale, so drop it too.
  const refetchAuth = query.refetch;
  useEffect(() => {
    if (!getImpersonatedMemberId()) {
      return;
    }
    if (query.isError) {
      setImpersonatedMemberId(null);
      void refetchAuth();
    } else if (query.isSuccess && !impersonator) {
      setImpersonatedMemberId(null);
    }
  }, [query.isError, query.isSuccess, impersonator, refetchAuth]);

  const can = useCallback(
    (area: RbacArea, level: RbacLevel = 'view'): boolean =>
      principal ? levelSatisfies(principal.permissions[area], level) : false,
    [principal],
  );

  const isAdmin = principal?.isAdmin ?? false;
  const menuPermissions = principal?.menuPermissions ?? {};
  const canSeeMenu = useCallback(
    (key: string): boolean => canSeeMenuItem(menuPermissions, key, isAdmin),
    [menuPermissions, isAdmin],
  );

  const startImpersonation = useCallback(
    (memberId: string): void => {
      setImpersonatedMemberId(memberId);
      // Re-resolve every cached query as the impersonated member.
      void queryClient.invalidateQueries();
    },
    [queryClient],
  );

  const stopImpersonation = useCallback((): void => {
    setImpersonatedMemberId(null);
    void queryClient.invalidateQueries();
  }, [queryClient]);

  const value = useMemo<AuthContextValue>(
    () => ({
      principal,
      roles,
      isLoading: query.isLoading,
      isError: query.isError,
      error: query.error,
      isAdmin: principal?.isAdmin ?? false,
      isTimeApprover: principal?.isTimeApprover ?? false,
      can,
      canSeeMenu,
      refetch: () => void query.refetch(),
      impersonator,
      startImpersonation,
      stopImpersonation,
    }),
    [
      principal,
      roles,
      query.isLoading,
      query.isError,
      query.error,
      query.refetch,
      can,
      canSeeMenu,
      impersonator,
      startImpersonation,
      stopImpersonation,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
